import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import { MODE_SETTING, GETING_DATA_BY_LOCALSTORAGE } from "../../actions";
import "../../assets/css/admin-iodin.css";

// Import icons (using existing ones from the platform)
import dashboardIcon from "../../assets/images/image-gallery/dashboard.svg";
import clientsIcon from "../../assets/images/Users_icon.svg";
import userManagementIcon from "../../assets/images/user_settings.svg";
import demoAccountIcon from "../../assets/images/guidance.svg";
import reportsIcon from "../../assets/images/UserGuide_CommonMenu.svg";
import settingsIcon from "../../assets/images/settings_mode.svg";
import analyticsIcon from "../../assets/images/Quantum_IconMain.svg";
import toolsIcon from "../../assets/images/solution_center.svg";

const AdminStartScreen = ({ activeView = "overview" }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  
  const orgName = useSelector(
    (state) =>
      state?.summary?.localClient?.orgName || state?.summary?.clients?.data?.[0]?.orgName
  );
  
  const [selectedTile, setSelectedTile] = useState(null);
  const [isModal, setModal] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Initialize demo org for admin users on component mount
  useEffect(() => {
    const authUser = JSON.parse(localStorage.getItem('user') || '{}');
    if (authUser.accessLevel === 'Admin' && activeView === 'overview') {
      const currentOrg = localStorage.getItem('org');
      if (!currentOrg || currentOrg === 'null') {
        const demoOrgId = '6634ae5446bf0060e41d6e67';
        const clients = JSON.parse(localStorage.getItem('clients') || '[]');
        const actualDemoOrg = clients.find(client => client._id === demoOrgId);
        
        const demoOrg = actualDemoOrg || { 
          _id: demoOrgId, 
          orgName: 'Demo Account',
          website: 'demo.iodin.io',
          description: 'Demo Organization for Testing and Presentations'
        };
        
        localStorage.setItem('org', JSON.stringify(demoOrg));
        dispatch({
          type: GETING_DATA_BY_LOCALSTORAGE,
          localClient: demoOrg,
        });
      }
    }
  }, [activeView, dispatch]);

  // Overview page admin tools - simplified
  const overviewTools = [
    {
      id: 'dashboard',
      title: 'Dashboard',
      subtitle: 'Standard Admin Dashboard',
      description: 'Access the main iOdin dashboard with all monitoring tools and assessment capabilities',
      icon: dashboardIcon,
      action: () => {
        // For admin users, ensure an organization is selected (default to demo if none)
        const currentOrg = localStorage.getItem('org');
        if (!currentOrg || currentOrg === 'null') {
          const demoOrgId = '6634ae5446bf0060e41d6e67';
          const clients = JSON.parse(localStorage.getItem('clients') || '[]');
          const actualDemoOrg = clients.find(client => client._id === demoOrgId);
          
          const demoOrg = actualDemoOrg || { 
            _id: demoOrgId, 
            orgName: 'Demo Account',
            website: 'demo.iodin.io',
            description: 'Demo Organization for Testing and Presentations'
          };
          
          localStorage.setItem('org', JSON.stringify(demoOrg));
          dispatch({
            type: GETING_DATA_BY_LOCALSTORAGE,
            localClient: demoOrg,
          });
        }
        
        dispatch({ type: MODE_SETTING, data: 'assessment-mode' });
        history.push('/summary');
      }
    },
    {
      id: 'demo',
      title: 'Demo Account',
      subtitle: 'Demo Dashboard Access',
      description: 'Open the dashboard as the Demo Account for presentations and demonstrations',
      icon: demoAccountIcon,
      action: () => {
        // Set demo org as the active organization
        const demoOrgId = '6634ae5446bf0060e41d6e67';
        
        // Try to find the actual demo org from the client list first
        const clients = JSON.parse(localStorage.getItem('clients') || '[]');
        const actualDemoOrg = clients.find(client => client._id === demoOrgId);
        
        const demoOrg = actualDemoOrg || { 
          _id: demoOrgId, 
          orgName: 'Demo Account',
          website: 'demo.iodin.io',
          description: 'Demo Organization for Testing and Presentations'
        };
        
        // Set in localStorage so it becomes the active client
        localStorage.setItem('org', JSON.stringify(demoOrg));
        localStorage.setItem('demoOrgId', demoOrgId);
        
        // Update Redux state
        dispatch({
          type: GETING_DATA_BY_LOCALSTORAGE,
          localClient: demoOrg,
        });
        
        // Clear any assessment mode and set to demo
        dispatch({ type: MODE_SETTING, data: 'demo-mode' });
        history.push('/summary');
      }
    },
    {
      id: 'clients',
      title: 'Client Management',
      subtitle: 'Manage Organizations',
      description: 'View and manage all client accounts, organizations, and their configurations',
      icon: clientsIcon,
      action: () => {
        history.push('/client');
      }
    },
    {
      id: 'userManagement',
      title: 'User Management',
      subtitle: 'Keycloak Admin Console',
      description: 'Manage users, roles, permissions and authentication through Keycloak admin interface',
      icon: userManagementIcon,
      action: () => {
        window.open('https://login.iodin.io/admin/iOdin/console/', '_blank');
      }
    }
  ];

  // Reports page tools
  const reportsTools = [
    {
      id: 'systemReports',
      title: 'System Reports',
      subtitle: 'Analytics & Insights',
      description: 'View comprehensive system-wide reports, analytics and performance insights',
      icon: reportsIcon,
      action: () => {
        // Implementation for system reports
      }
    },
    {
      id: 'usageAnalytics',
      title: 'Usage Analytics',
      subtitle: 'Platform Statistics',
      description: 'View detailed platform usage statistics, user behavior and system insights',
      icon: analyticsIcon,
      action: () => {
        // Implementation for usage analytics
      }
    }
  ];

  // Settings page tools
  const settingsTools = [
    {
      id: 'systemSettings',
      title: 'System Settings',
      subtitle: 'Platform Configuration',
      description: 'Configure system-wide settings, parameters and platform preferences',
      icon: settingsIcon,
      action: () => {
        // Implementation for system settings
      }
    }
  ];

  // Tools page tools
  const toolsPageTools = [
    {
      id: 'osintTools',
      title: 'OSINT Tools',
      subtitle: 'Open Source Intelligence',
      description: 'Access various OSINT tools for intelligence gathering and reconnaissance',
      icon: toolsIcon,
      action: () => {
        // Implementation for OSINT tools
      }
    }
  ];

  // Sidebar menu items - simplified
  const sidebarMenuItems = [
    { id: 'overview', label: 'Overview', icon: dashboardIcon },
    { id: 'settings', label: 'Settings', icon: settingsIcon },
    { id: 'reports', label: 'Reports', icon: reportsIcon },
    { id: 'tools', label: 'Tools', icon: toolsIcon }
  ];

  // Get current tools based on active view
  const getCurrentTools = () => {
    switch (activeView) {
      case 'reports':
        return reportsTools;
      case 'settings':
        return settingsTools;
      case 'tools':
        return toolsPageTools;
      default:
        return overviewTools;
    }
  };

  // Get current view title and description
  const getViewInfo = () => {
    switch (activeView) {
      case 'reports':
        return { title: 'Reports', description: 'System reports and analytics dashboard' };
      case 'settings':
        return { title: 'Settings', description: 'System configuration and platform settings' };
      case 'tools':
        return { title: 'Tools', description: 'OSINT and administrative tools collection' };
      default:
        return { title: 'Dashboard Overview', description: 'Manage your iOdin platform with administrative tools and controls' };
    }
  };

  const handleTileClick = (tool) => {
    setSelectedTile(tool.id);
    setTimeout(() => {
      tool.action();
      setSelectedTile(null);
    }, 200);
  };

  const handleSidebarNavigation = (viewId) => {
    history.push(`/admin/${viewId === 'overview' ? '' : viewId}`);
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleLogout = () => {
    setModal(true);
  };

  const LogoutModal = ({ isModal, setModal }) => (
    <div className={`modal fade  ${isModal ? "d-block show" : ""}`} id="exampleModal">
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id="exampleModalLabel">
              Log out?
            </h5>
            <button
              type="button"
              className="close"
              data-dismiss="modal"
              aria-label="Close"
              onClick={() => setModal(false)}
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>
          <div className="modal-body">
            <p>Are you sure you want to Logout?</p>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-round btn-default"
              data-dismiss="modal"
              onClick={() => setModal(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-round btn-primary"
              onClick={() => {
                localStorage.clear();
                window.location.replace("/login");
              }}
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Render different content based on active view
  const renderContent = () => {
    const viewInfo = getViewInfo();
    const tools = getCurrentTools();

    if (activeView === 'reports' || activeView === 'settings' || activeView === 'tools') {
      return (
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <h2>{viewInfo.title}</h2>
                  <p>{viewInfo.description}</p>
                  
                  {activeView === 'tools' ? (
                    <div className="admin_tools_grid">
                      <div className="tools_row">
                        {tools.map((tool) => (
                          <div 
                            key={tool.id} 
                            className={`admin_tool_card ${selectedTile === tool.id ? 'selected' : ''}`}
                            onClick={() => handleTileClick(tool)}
                          >
                            <div className="tool_icon">
                              <img src={tool.icon} alt={tool.title} />
                            </div>
                            <div className="tool_content">
                              <h4 className="tool_title">{tool.title}</h4>
                              <p className="tool_subtitle">{tool.subtitle}</p>
                              <p className="tool_description">{tool.description}</p>
                            </div>
                            <div className="tool_arrow">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="alert alert-info">
                      <strong>Coming Soon!</strong> This section is currently under development and will be available in future updates.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Overview content
    return (
      <div className="admin_content">
        <div className="content_header">
          <h2>{viewInfo.title}</h2>
          <p>{viewInfo.description}</p>
        </div>

        {/* Admin Tools Grid */}
        <div className="admin_tools_grid">
          <div className="tools_section">
            <div className="tools_row">
              {tools.map((tool) => (
                <div 
                  key={tool.id} 
                  className={`admin_tool_card ${selectedTile === tool.id ? 'selected' : ''}`}
                  onClick={() => handleTileClick(tool)}
                >
                  <div className="tool_icon">
                    <img src={tool.icon} alt={tool.title} />
                  </div>
                  <div className="tool_content">
                    <h4 className="tool_title">{tool.title}</h4>
                    <p className="tool_subtitle">{tool.subtitle}</p>
                    <p className="tool_description">{tool.description}</p>
                  </div>
                  <div className="tool_arrow">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* System Status Section - Only show on overview */}
        <div className="system_status_section">
          <h3 className="section_title">System Status</h3>
          <div className="status_grid">
            <div className="status_card">
              <div className="status_indicator active"></div>
              <div className="status_info">
                <span className="status_label">Platform Status</span>
                <span className="status_value">Active & Running</span>
              </div>
            </div>
            <div className="status_card">
              <div className="status_indicator active"></div>
              <div className="status_info">
                <span className="status_label">Security Level</span>
                <span className="status_value">Secure</span>
              </div>
            </div>
            <div className="status_card">
              <div className="status_indicator active"></div>
              <div className="status_info">
                <span className="status_label">Monitoring</span>
                <span className="status_value">24/7 Active</span>
              </div>
            </div>
            <div className="status_card">
              <div className="status_indicator active"></div>
              <div className="status_info">
                <span className="status_label">Version</span>
                <span className="status_value">Latest</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="admin_control_panel">
      {/* Admin Top Bar */}
      <div className="admin_top_bar">
        <div className="admin_top_left">
          <button className="sidebar_toggle" onClick={toggleSidebar}>
            <span></span>
            <span></span>
            <span></span>
          </button>
          <div className="admin_logo">
            <img src="../assets/images/image-gallery/logo.svg" className="img-fluid" alt="iOdin Logo"/>
          </div>
          <h1 className="admin_panel_title">Admin Control Panel</h1>
        </div>
        
        <div className="admin_top_right">
          <div className="admin_user_info">
            <span className="user_label">Signed in as:</span>
            <span className="user_name">{orgName}</span>
          </div>
          <button className="admin_logout_btn" onClick={handleLogout}>
            <img src="../assets/images/image-gallery/signout.svg" alt=""/>
            <span>Sign Out</span>
          </button>
        </div>
      </div>

      {/* Main Admin Layout */}
      <div className="admin_main_layout">
        {/* Sidebar */}
        <div className={`admin_sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <div className="sidebar_header">
            <h3>Admin Menu</h3>
          </div>
          <nav className="sidebar_nav">
            {sidebarMenuItems.map(item => (
              <div 
                key={item.id} 
                className={`sidebar_item ${activeView === item.id ? 'active' : ''}`}
                onClick={() => handleSidebarNavigation(item.id)}
              >
                <img src={item.icon} alt={item.label} className="sidebar_icon"/>
                <span className="sidebar_label">{item.label}</span>
              </div>
            ))}
          </nav>
        </div>

        {/* Main Content */}
        {renderContent()}
      </div>

      <LogoutModal isModal={isModal} setModal={setModal} />
    </div>
  );
};

// AdminStartScreen component with default activeView prop

export default AdminStartScreen;
